import request, {
  convertData,
  DataSource,
  MutationOptions,
  useMakeMutation,
} from '@shared/innmaxLib/services'
import { indexBy, prop } from 'ramda'
import React from 'react'
import { useQuery } from 'react-query'
import { useConvertCompany } from './companies.converter'
import { ICompanyRes } from './companies.type'

export const useAddCompany = (options: MutationOptions) => {
  return useMakeMutation(
    (body: any) =>
      request('/orgSessionInfo', {
        method: 'POST',
        body: body,
      }),
    options
  )
}

export const useEditCompany = (options: MutationOptions) => {
  return useMakeMutation(
    (body: any) =>
      request('/orgSessionInfo', {
        method: 'PUT',
        body: body,
      }),
    options
  )
}

export const useDeleteCompany = (options: MutationOptions) => {
  return useMakeMutation(
    (body: any) =>
      request('/orgSessionInfo', {
        method: 'DELETE',
        body: body,
      }),
    options
  )
}

export const useGetCompanyTree = (query?: any) => {
  const { data: _data, ...others } = useQuery(
    ['/orgSessionInfo/tree/all', query],
    {}
  )

  const converter = useConvertCompany()

  const { dataSource, byId } = React.useMemo(() => {
    const filteredData = (_data as any)?.data?.filter(
      (i: ICompanyRes) => i.name !== '未分區'
    )
    const dataSource: DataSource<ICompanyRes> = convertData(
      {
        field: ['data'],
        converter,
      },
      { data: filteredData }
    )

    const byId = indexBy(prop('id'), dataSource.content || [])

    return { dataSource, byId }
  }, [_data])

  return {
    dataSource,
    byId,
    ...others,
  }
}

export const useGetCompanies = (query?: any) => {
  const { data, ...others } = useQuery(['/orgSessionInfo/tree/all', query], {
    select: (res: any) => res.data,
  })

  const converter = useConvertCompany()

  const utils = React.useMemo(() => {
    const _data: ICompanyRes[] = data || []
    const rootCompany = _data.find((i: ICompanyRes) => i.subAvailable)
    const dataSource: DataSource<ICompanyRes> = convertData(
      {
        field: ['sessions'],
        converter,
      },
      rootCompany
    )
    const flattenSessions = (sessions: ICompanyRes[]): ICompanyRes[] => {
      return sessions.reduce((acc: ICompanyRes[], session: ICompanyRes) => {
        acc.push(session)
        if (session.sessions && session.sessions.length > 0) {
          acc.push(...flattenSessions(session.sessions))
        }
        return acc
      }, [])
    }

    const flattenCompanies: ICompanyRes[] = flattenSessions(
      dataSource.content || []
    )

    const byId = indexBy(prop('id'), flattenCompanies || [])

    const toFullPathName = (id: number | undefined) => {
      if (!id) {
        return ''
      }
      const names: string[] = []
      let current = byId[id]
      while (current) {
        names.unshift(current.name)
        current = byId[current.parentSessionId]
      }
      return names.join('-')
    }

    return { rootCompany, dataSource, byId, flattenCompanies, toFullPathName }
  }, [data])

  return {
    ...utils,
    ...others,
  }
}

export const useGetCompaniesById = (query: { id: number }) => {
  const { data: _data, ...others } = useQuery(
    ['/orgSessionInfo/detail', query],
    {
      enabled: !!query.id,
    }
  )

  const converter = useConvertCompany()

  const data = React.useMemo(() => {
    if (!(_data as any)?.data) {
      return null
    }
    return converter((_data as any).data)
  }, [_data])

  return {
    data,
    ...others,
  }
}
